import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTeams } from '../../hooks/useTeams'
import { useMatches } from '../../hooks/useMatches'
import { Button, Input, Modal, Badge, DarkModeToggleCompact } from '../../components/ui'
import { ArrowLeft, Trophy, ArrowUpCircle, ArrowDownCircle, Shuffle, Save, Settings, Plus, History, Calendar, Swords } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { BracketGenerator } from '../../components/tools/BracketGenerator'
import { usePoules } from '../../hooks/usePoules'
import { useTournaments } from '../../hooks/useTournaments'
import { useTranslation } from 'react-i18next'

export const LadderPage = () => {
    const { t } = useTranslation()
    const { tournamentId } = useParams()
    const navigate = useNavigate()
    const { teams, loading: teamsLoading } = useTeams(tournamentId)
    const { matches, createMatch, refetch: refetchMatches } = useMatches(tournamentId)

    const [ladderTeams, setLadderTeams] = useState([])
    const [challengeRange, setChallengeRange] = useState(() => {
        return parseInt(localStorage.getItem(`ladder_range_${tournamentId}`)) || 3
    })

    // Match Modal State
    const [showMatchModal, setShowMatchModal] = useState(false)
    const [matchForm, setMatchForm] = useState({
        home_team_id: '',
        away_team_id: '',
        score_home: '',
        score_away: ''
    })

    const handleRangeChange = (e) => {
        const val = parseInt(e.target.value)
        setChallengeRange(val)
        localStorage.setItem(`ladder_range_${tournamentId}`, val)
    }

    useEffect(() => {
        if (teams.length > 0) {
            // Sort by ladder_rank. If null, put at bottom.
            const sorted = [...teams].sort((a, b) => {
                if (a.ladder_rank === null && b.ladder_rank === null) return 0
                if (a.ladder_rank === null) return 1
                if (b.ladder_rank === null) return -1
                return a.ladder_rank - b.ladder_rank
            })
            setLadderTeams(sorted)
        }
    }, [teams])

    const historyMatches = matches
        .filter(m => m.type === 'ladder')
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))

    const initializeLadder = async () => {
        if (!confirm(t('ladder.confirm_shuffle'))) return

        const shuffled = [...teams].sort(() => Math.random() - 0.5)
        const updates = shuffled.map((team, index) => ({
            id: team.id,
            ladder_rank: index + 1
        }))

        const newLadder = shuffled.map((team, index) => ({ ...team, ladder_rank: index + 1 }))
        setLadderTeams(newLadder)

        for (let update of updates) {
            await supabase.from('teams').update({ ladder_rank: update.ladder_rank }).eq('id', update.id)
        }
    }

    const swapRanks = async (index1, index2) => {
        const newLadder = [...ladderTeams]
        const team1 = newLadder[index1]
        const team2 = newLadder[index2]

        const rank1 = team1.ladder_rank
        const rank2 = team2.ladder_rank

        team1.ladder_rank = rank2
        team2.ladder_rank = rank1

        newLadder[index1] = team2
        newLadder[index2] = team1

        setLadderTeams(newLadder)

        console.log(`SWAPPING: Updating ${team1.name} (ID: ${team1.id}) to Rank ${team1.ladder_rank}`);
        console.log(`SWAPPING: Updating ${team2.name} (ID: ${team2.id}) to Rank ${team2.ladder_rank}`);

        await supabase.from('teams').update({ ladder_rank: team1.ladder_rank }).eq('id', team1.id)
        await supabase.from('teams').update({ ladder_rank: team2.ladder_rank }).eq('id', team2.id)
    }

    const handleMatchSubmit = async (e) => {
        e.preventDefault()
        if (!matchForm.home_team_id || !matchForm.away_team_id) {
            alert(t('ladder.alert_choose_teams'))
            return
        }
        if (matchForm.home_team_id === matchForm.away_team_id) {
            alert(t('ladder.alert_different_teams'))
            return
        }
        if (matchForm.score_home === '' || matchForm.score_away === '') {
            alert(t('ladder.alert_enter_scores'))
            return
        }

        try {
            await createMatch({
                type: 'ladder',
                home_team_id: matchForm.home_team_id,
                away_team_id: matchForm.away_team_id,
                score_home: parseInt(matchForm.score_home),
                score_away: parseInt(matchForm.score_away),
                round_name: t('ladder.match_type_name')
            })

            const indexHome = ladderTeams.findIndex(t => t.id === matchForm.home_team_id)
            const indexAway = ladderTeams.findIndex(t => t.id === matchForm.away_team_id)

            if (indexHome === -1 || indexAway === -1) return

            const lowerIndex = Math.max(indexHome, indexAway)
            const higherIndex = Math.min(indexHome, indexAway)

            const lowerTeamId = ladderTeams[lowerIndex].id
            const winnerId = parseInt(matchForm.score_home) > parseInt(matchForm.score_away)
                ? matchForm.home_team_id
                : parseInt(matchForm.score_away) > parseInt(matchForm.score_home)
                    ? matchForm.away_team_id
                    : null

            if (winnerId === lowerTeamId) {
                await swapRanks(lowerIndex, higherIndex)
                alert(t('ladder.success_swap'))
            } else {
                alert(t('ladder.success_no_swap'))
            }

            setShowMatchModal(false)
            setMatchForm({ home_team_id: '', away_team_id: '', score_home: '', score_away: '' })
            refetchMatches()

        } catch (err) {
            alert(err.message)
        }
    }

    return (
        <div className="min-h-screen">
            <div className="premium-container">
                {/* Hero Section */}
                <div className="mb-12 animate-slideUp">
                    <Button variant="ghost" onClick={() => navigate('/admin/dashboard')} className="mb-4">
                        <ArrowLeft size={16} className="mr-2" />
                        {t('ladder.btn_back')}
                    </Button>

                    <div className="flex justify-between items-start mb-6">
                        <div className="flex items-center gap-4">
                            <div className="p-3 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-500 shadow-lg">
                                <ArrowUpCircle className="text-white" size={32} />
                            </div>
                            <div>
                                <h1 className="text-5xl font-bold text-gray-900 leading-tight">
                                    {t('ladder.title')}
                                </h1>
                                <p className="text-lg text-gray-600 mt-1">{t('ladder.subtitle')}</p>
                            </div>
                        </div>

                        <div className="flex items-center gap-3">
                            <DarkModeToggleCompact />
                            <Button onClick={() => setShowMatchModal(true)} variant="primary" icon={<Swords size={20} />}>
                                {t('ladder.btn_enter_result')}
                            </Button>
                        </div>
                    </div>
                </div>

                <div className="glass-strong premium-card rounded-2xl p-6 md:p-8 animate-slideUp" style={{ animationDelay: '0.1s' }}>
                    <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-6 border-b border-white/20 pb-6">
                        <div className="flex-1 bg-blue-50/50 p-4 rounded-xl border border-blue-100 backdrop-blur-sm">
                            <p className="text-gray-700 text-sm mb-2 flex items-center gap-2">
                                <span className="p-1 bg-blue-100 text-blue-600 rounded-lg"><Settings size={14} /></span>
                                <strong>{t('ladder.rules_title')}</strong>
                            </p>
                            <p className="text-gray-600 text-sm" dangerouslySetInnerHTML={{ __html: t('ladder.rules_text', { range: challengeRange }) }} />
                            <div className="mt-3 flex items-center gap-2">
                                <span className="text-xs text-gray-500 uppercase font-bold tracking-wide">{t('ladder.label_range')}</span>
                                <select
                                    value={challengeRange}
                                    onChange={handleRangeChange}
                                    className="bg-white border border-gray-200 text-sm font-bold rounded-lg py-1 px-3 focus:ring-2 focus:ring-blue-500 outline-none cursor-pointer hover:border-blue-400 transition-colors"
                                >
                                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
                                        <option key={n} value={n}>{n} {t('ladder.unit_spots')}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <Button onClick={initializeLadder} variant="secondary" icon={<Shuffle size={18} />}>
                            {t('ladder.btn_shuffle')}
                        </Button>
                    </div>

                    <div className="space-y-4 relative mb-12 px-2">
                        {/* Connecting Line (Premium Visual) */}
                        <div className="absolute left-[2.25rem] top-4 bottom-4 w-1 bg-gradient-to-b from-yellow-400 via-gray-300 to-gray-200 -z-10 rounded-full opacity-50"></div>

                        {ladderTeams.map((team, index) => (
                            <div key={team.id} className="flex items-center gap-6 transition-all hover:scale-[1.01] group">
                                <div className={`flex-shrink-0 w-16 h-16 rounded-2xl rotate-3 flex items-center justify-center text-2xl font-black text-white shadow-lg z-10 
                                    ${index === 0 ? 'bg-gradient-to-br from-yellow-300 to-yellow-500 ring-4 ring-yellow-100' :
                                        index === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-400 ring-4 ring-gray-100' :
                                            index === 2 ? 'bg-gradient-to-br from-orange-300 to-orange-400 ring-4 ring-orange-100' :
                                                'bg-white text-gray-400 border-2 border-gray-200 shadow-sm'}`}>
                                    {index === 0 ? <Trophy size={28} className="drop-shadow-md" /> : <span className="drop-shadow-sm">{index + 1}</span>}
                                </div>

                                <div className="flex-1 glass-subtle hover:bg-white rounded-xl p-4 shadow-sm border border-white/50 flex justify-between items-center transition-all group-hover:shadow-md">
                                    <div className="flex items-center gap-4">
                                        {team.logo_url ? (
                                            <img src={team.logo_url} className="w-12 h-12 rounded-xl object-cover bg-gray-100 shadow-sm" />
                                        ) : (
                                            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center text-gray-400 font-bold text-xl">
                                                {team.name.charAt(0)}
                                            </div>
                                        )}
                                        <div>
                                            <span className="font-bold text-lg text-gray-800 block">{team.name}</span>
                                            {index === 0 && <span className="text-xs font-bold text-yellow-600 uppercase tracking-wide bg-yellow-50 px-2 py-0.5 rounded-full">{t('ladder.rank_king')}</span>}
                                        </div>
                                    </div>

                                    <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity transform translate-x-4 group-hover:translate-x-0">
                                        {index > 0 && (
                                            <button
                                                onClick={() => swapRanks(index, index - 1)}
                                                className="p-2 bg-green-50 hover:bg-green-100 text-green-600 rounded-lg transition-colors shadow-sm"
                                                title={t('ladder.tooltip_move_up')}
                                            >
                                                <ArrowUpCircle size={20} />
                                            </button>
                                        )}
                                        {index < ladderTeams.length - 1 && (
                                            <button
                                                onClick={() => swapRanks(index, index + 1)}
                                                className="p-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg transition-colors shadow-sm"
                                                title={t('ladder.tooltip_move_down')}
                                            >
                                                <ArrowDownCircle size={20} />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* HISTORY SECTION */}
                    {historyMatches.length > 0 && (
                        <div className="mt-12 pt-8 border-t border-gray-200/50">
                            <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                                <History className="text-[var(--primary)]" /> {t('ladder.history_title')}
                            </h3>
                            <div className="grid gap-3">
                                {historyMatches.map(match => (
                                    <div key={match.id} className="flex items-center justify-between bg-white/60 p-4 rounded-xl border border-white/50 shadow-sm hover:shadow-md transition-all">
                                        <div className="flex items-center gap-4 flex-1 justify-end">
                                            <span className={`font-bold text-lg ${parseInt(match.score_home) > parseInt(match.score_away) ? 'text-green-600' : 'text-gray-600'}`}>
                                                {match.home_team?.name}
                                            </span>
                                            <span className="bg-white px-3 py-1.5 rounded-lg border border-gray-100 shadow-inner font-black text-xl min-w-[40px] text-center">{match.score_home}</span>
                                        </div>
                                        <span className="mx-6 text-gray-300 font-black text-xl">-</span>
                                        <div className="flex items-center gap-4 flex-1">
                                            <span className="bg-white px-3 py-1.5 rounded-lg border border-gray-100 shadow-inner font-black text-xl min-w-[40px] text-center">{match.score_away}</span>
                                            <span className={`font-bold text-lg ${parseInt(match.score_away) > parseInt(match.score_home) ? 'text-green-600' : 'text-gray-600'}`}>
                                                {match.away_team?.name}
                                            </span>
                                        </div>
                                        <div className="ml-6 text-xs font-medium text-gray-400 uppercase tracking-wider w-24 text-right">
                                            {match.created_at ? new Date(match.created_at).toLocaleDateString(t('public.date_locale'), { day: 'numeric', month: 'short' }) : t('ladder.history_just_now')}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <Modal isOpen={showMatchModal} onClose={() => setShowMatchModal(false)} title={t('ladder.modal_title')}>
                    <form onSubmit={handleMatchSubmit} className="space-y-6">
                        <div className="grid grid-cols-2 gap-6">
                            <div className="space-y-2">
                                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide">{t('ladder.label_home')}</label>
                                <select
                                    className="w-full p-3 border border-gray-200 rounded-xl bg-gray-50 focus:ring-2 focus:ring-[var(--primary)] outline-none transition-all font-medium"
                                    value={matchForm.home_team_id}
                                    onChange={e => setMatchForm({ ...matchForm, home_team_id: e.target.value })}
                                    required
                                >
                                    <option value="">{t('ladder.select_team_placeholder')}</option>
                                    {ladderTeams.map(t => <option key={t.id} value={t.id}>{t.name} (#{t.ladder_rank})</option>)}
                                </select>
                            </div>
                            <div className="space-y-2">
                                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide">{t('ladder.label_away')}</label>
                                <select
                                    className="w-full p-3 border border-gray-200 rounded-xl bg-gray-50 focus:ring-2 focus:ring-[var(--primary)] outline-none transition-all font-medium"
                                    value={matchForm.away_team_id}
                                    onChange={e => setMatchForm({ ...matchForm, away_team_id: e.target.value })}
                                    required
                                >
                                    <option value="">{t('ladder.select_team_placeholder')}</option>
                                    {ladderTeams.map(t => <option key={t.id} value={t.id}>{t.name} (#{t.ladder_rank})</option>)}
                                </select>
                            </div>
                        </div>

                        <div className="flex items-center justify-center gap-6">
                            <div className="text-center">
                                <label className="block text-xs font-bold text-gray-400 mb-2">{t('ladder.label_score')}</label>
                                <input
                                    type="number"
                                    className="w-24 h-24 text-center text-4xl font-black border-2 border-gray-100 rounded-2xl focus:border-[var(--primary)] outline-none shadow-sm bg-gray-50"
                                    value={matchForm.score_home}
                                    onChange={e => setMatchForm({ ...matchForm, score_home: e.target.value })}
                                    placeholder="0"
                                    required
                                />
                            </div>
                            <span className="text-2xl font-black text-gray-300 mt-6">-</span>
                            <div className="text-center">
                                <label className="block text-xs font-bold text-gray-400 mb-2">{t('ladder.label_score')}</label>
                                <input
                                    type="number"
                                    className="w-24 h-24 text-center text-4xl font-black border-2 border-gray-100 rounded-2xl focus:border-[var(--primary)] outline-none shadow-sm bg-gray-50"
                                    value={matchForm.score_away}
                                    onChange={e => setMatchForm({ ...matchForm, score_away: e.target.value })}
                                    placeholder="0"
                                    required
                                />
                            </div>
                        </div>

                        <div className="pt-4">
                            <Button type="submit" variant="primary" className="w-full py-4 text-lg shadow-xl" icon={<Save size={20} />}>
                                {t('ladder.btn_save_process')}
                            </Button>
                        </div>
                        <div className="bg-blue-50 p-4 rounded-xl text-xs text-blue-800 text-center leading-relaxed">
                            <strong>{t('ladder.how_it_works_title')}</strong><br />
                            <span dangerouslySetInnerHTML={{ __html: t('ladder.how_it_works_text') }} />
                        </div>
                    </form>
                </Modal>
            </div>
        </div >
    )
}
